import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ArrowRightLeft, Star, ImageOff, ZoomIn, X as XIcon } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useLanguage } from '../contexts/LanguageContext';
import { API_BASE_URL } from '../utils/apiBase';
import * as localStore from '../services/localPlantStore';

const BACKEND_URL = API_BASE_URL;

// The full care-detail fields exposed on the diff UI. Kept in this order
// so the resolved payload matches the shape server-side validators
// expect. Each entry maps the on-disk snake_case name to the camelCase
// i18n key used in translations.js.
const CARE_FIELDS = [
  { id: 'watering', i18n: 'watering' },
  { id: 'light', i18n: 'light' },
  { id: 'humidity', i18n: 'humidity' },
  { id: 'temperature', i18n: 'temperature' },
  { id: 'toxicity', i18n: 'toxicity' },
  { id: 'toxicity_notes', i18n: 'toxicityNotes' },
  { id: 'growth_rate', i18n: 'growthRate' },
  { id: 'family', i18n: 'family' },
  { id: 'plant_type', i18n: 'plantType' },
  { id: 'environment', i18n: 'environment' },
  { id: 'dormancy_notes', i18n: 'dormancyNotes' },
  { id: 'bloom_season', i18n: 'bloomSeason' },
  { id: 'bloom_duration', i18n: 'bloomDuration' },
  { id: 'blooms_indoors', i18n: 'bloomsIndoors' },
  { id: 'bloom_tips', i18n: 'bloomTips' },
];
const CARE_FIELD_IDS = CARE_FIELDS.map(f => f.id);
const I18N_BY_FIELD = Object.fromEntries(CARE_FIELDS.map(f => [f.id, f.i18n]));

// Non-destructive care_details merge: target wins on any set field; the
// source fills BLANKS on the target so we don't silently lose info the
// Absorbed plant had that the Keeper didn't. This is applied when the
// user hits Merge — no UI shown for it because "if they're the same
// species the care values shouldn't differ" (per user Feb 2026 request).
const buildResolvedCare = (target, source) => {
  const tCare = target?.care_details || {};
  const sCare = source?.care_details || {};
  const res = {};
  for (const field of CARE_FIELD_IDS) {
    const tVal = tCare[field];
    const sVal = sCare[field];
    if (tVal && String(tVal).trim()) res[field] = tVal;
    else if (sVal && String(sVal).trim()) res[field] = sVal;
  }
  return res;
};

export const MergePlantsDialog = ({ target, source, onClose, onMerged, isLocalMode = false }) => {
  const { t } = useLanguage();
  const [merging, setMerging] = useState(false);
  const [commonName, setCommonName] = useState(target?.common_name || '');
  const [scientificName, setScientificName] = useState(target?.scientific_name || source?.scientific_name || '');

  // Photo picker. All photos from BOTH plants are loaded up front so
  // the user can visually confirm they're the right species and choose
  // exactly which ones survive the merge. `photos` is a flat list of
  // { key, source: 'target'|'source', plantId, index, drive_id?, image_base64? };
  // in local mode we have base64 payloads to render inline, in Drive
  // mode we render via the /api/plants/{id}/photos/{drive_id} URL.
  const [photos, setPhotos] = useState([]);
  const [photosLoading, setPhotosLoading] = useState(true);
  // `selectedKeys` is an ordered array (insertion order = cover order)
  // so the FIRST selected photo becomes the merged plant's cover.
  const [selectedKeys, setSelectedKeys] = useState([]);
  // Full-screen preview of a single photo when the user taps its
  // magnifier icon. Null when closed; otherwise `photo` object.
  const [zoomedPhoto, setZoomedPhoto] = useState(null);
  const MAX_PICK = 5;

  useEffect(() => {
    let cancelled = false;
    setPhotosLoading(true);
    (async () => {
      try {
        let combined = [];
        if (isLocalMode) {
          const [tp, sp] = await Promise.all([
            localStore.getAllPhotos(target.plant_id),
            localStore.getAllPhotos(source.plant_id),
          ]);
          combined = [
            ...tp.map(p => ({ key: `t-${target.plant_id}-${p.index}`, source: 'target', plantId: target.plant_id, index: p.index, image_base64: p.image_base64, thumb: p.thumbnail_base64 ? `data:image/jpeg;base64,${p.thumbnail_base64}` : (p.image_base64 ? `data:image/jpeg;base64,${p.image_base64}` : null) })),
            ...sp.map(p => ({ key: `s-${source.plant_id}-${p.index}`, source: 'source', plantId: source.plant_id, index: p.index, image_base64: p.image_base64, thumb: p.thumbnail_base64 ? `data:image/jpeg;base64,${p.thumbnail_base64}` : (p.image_base64 ? `data:image/jpeg;base64,${p.image_base64}` : null) })),
          ];
        } else {
          // Drive mode — fetch each photo via the existing
          // /api/photos/{drive_id} endpoint using the session token in
          // the Authorization header (matches PlantDetailModal's loader).
          // Blob-URLs are used so <img src=...> works without a
          // browser-visible token in the URL.
          const sessionToken = localStorage.getItem('auth_hint');
          const tIds = target?.photo_drive_ids || (target?.photo_drive_id ? [target.photo_drive_id] : []);
          const sIds = source?.photo_drive_ids || (source?.photo_drive_id ? [source.photo_drive_id] : []);
          const fetchThumb = async (did) => {
            try {
              const resp = await axios.get(`${BACKEND_URL}/api/photos/${did}`, {
                headers: { Authorization: `Bearer ${sessionToken}` },
                responseType: 'blob',
              });
              return URL.createObjectURL(resp.data);
            } catch { return null; }
          };
          const tThumbs = await Promise.all(tIds.map(fetchThumb));
          const sThumbs = await Promise.all(sIds.map(fetchThumb));
          combined = [
            ...tIds.map((did, i) => ({ key: `t-${did}`, source: 'target', plantId: target.plant_id, index: i, drive_id: did, thumb: tThumbs[i] })),
            ...sIds.map((did, i) => ({ key: `s-${did}`, source: 'source', plantId: source.plant_id, index: i, drive_id: did, thumb: sThumbs[i] })),
          ];
        }
        if (cancelled) return;
        setPhotos(combined);
        // Default selection: target photos first (up to 5), source next.
        // This preserves the original carousel behaviour when the user
        // doesn't customise anything.
        const defaults = [];
        for (const p of combined) {
          if (p.source === 'target' && defaults.length < MAX_PICK) defaults.push(p.key);
        }
        for (const p of combined) {
          if (p.source === 'source' && defaults.length < MAX_PICK && !defaults.includes(p.key)) defaults.push(p.key);
        }
        setSelectedKeys(defaults);
      } catch (err) {
        console.warn('MergePlantsDialog: photo load failed:', err?.message || err);
      } finally {
        if (!cancelled) setPhotosLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isLocalMode, target, source]);

  // Photo counts derived from the loaded list (works for both modes).
  const targetPhotoCount = photos.filter(p => p.source === 'target').length;
  const sourcePhotoCount = photos.filter(p => p.source === 'source').length;
  const combinedPhotoEstimate = selectedKeys.length;
  const droppedPhotoEstimate = Math.max(0, photos.length - selectedKeys.length);

  const togglePhoto = (key) => {
    setSelectedKeys((prev) => {
      if (prev.includes(key)) {
        return prev.filter(k => k !== key);
      }
      // Enforce 5-max: auto-drop the OLDEST selection with a toast so
      // the user knows what happened.
      if (prev.length >= MAX_PICK) {
        toast(t('mergePhotoAutoDrop') || `Max ${MAX_PICK} photos — the oldest selection was unchecked.`);
        return [...prev.slice(1), key];
      }
      return [...prev, key];
    });
  };

  const makeCover = (key) => {
    // Move `key` to the front of the selection so it becomes cover.
    // If it isn't selected yet, select AND make cover in one tap.
    setSelectedKeys((prev) => {
      const without = prev.filter(k => k !== key);
      if (!prev.includes(key) && without.length >= MAX_PICK) {
        toast(t('mergePhotoAutoDrop') || `Max ${MAX_PICK} photos — the oldest selection was unchecked.`);
        return [key, ...without.slice(1)];
      }
      return [key, ...without];
    });
  };

  const handleMerge = async () => {
    if (!target || !source) return;
    if (target.plant_id === source.plant_id) {
      toast.error(t('mergeSameError') || "Can't merge a plant with itself.");
      return;
    }
    // Non-destructive care-field merge — target wins on set fields;
    // source fills blanks. No per-field UI (Feb 2026: same-species
    // plants shouldn't diverge).
    const resolvedCare = buildResolvedCare(target, source);

    // Snapshot BOTH plants BEFORE the merge so a 5-second "Undo" toast
    // can restore them. Drive-mode: we capture the plant records + the
    // drive_ids of every photo, which still exist on Drive after the
    // merge (explicit-picker mode doesn't physically delete anything).
    // Local-mode: we also capture each photo's base64 payload so we can
    // rebuild IndexedDB entirely from the snapshot.
    const buildSnapshot = async () => {
      const cap = (plant) => ({
        plant_id: plant.plant_id,
        common_name: plant.common_name,
        scientific_name: plant.scientific_name || '',
        care_details: plant.care_details || {},
        photo_drive_ids: plant.photo_drive_ids || (plant.photo_drive_id ? [plant.photo_drive_id] : []),
        image_hashes: plant.image_hashes || (plant.image_hash ? [plant.image_hash] : []),
        incomplete: !!plant.incomplete,
        archived: !!plant.archived,
        created_at: plant.created_at,
      });
      const snap = { target: cap(target), source: cap(source) };
      if (isLocalMode) {
        const [tPhotos, sPhotos] = await Promise.all([
          localStore.getAllPhotos(target.plant_id),
          localStore.getAllPhotos(source.plant_id),
        ]);
        snap.target.local_photos = tPhotos.map(p => ({ index: p.index, image_base64: p.image_base64 }));
        snap.source.local_photos = sPhotos.map(p => ({ index: p.index, image_base64: p.image_base64 }));
      }
      return snap;
    };
    const snapshot = await buildSnapshot();

    setMerging(true);
    try {
      // Build the ordered selection payload for whichever mode.
      const selectedPhotos = selectedKeys
        .map(k => photos.find(p => p.key === k))
        .filter(Boolean);

      let mergeDropped = 0;
      if (isLocalMode) {
        const res = await localStore.mergePlants(target.plant_id, source.plant_id, {
          common_name: commonName.trim() || target.common_name,
          scientific_name: scientificName.trim() || '',
          care_details: resolvedCare,
          selectedPhotos: selectedPhotos.map(p => ({
            plantId: p.plantId, index: p.index, image_base64: p.image_base64,
          })),
        });
        mergeDropped = res.dropped_photo_count || 0;
      } else {
        const sessionToken = localStorage.getItem('auth_hint');
        const keepPhotoIds = selectedPhotos
          .map(p => p.drive_id)
          .filter(Boolean);
        const resp = await axios.post(
          `${BACKEND_URL}/api/plants/${encodeURIComponent(target.plant_id)}/merge`,
          {
            source_plant_id: source.plant_id,
            common_name: commonName.trim() || target.common_name,
            scientific_name: scientificName.trim() || '',
            care_details: resolvedCare,
            keep_photo_ids: keepPhotoIds,
          },
          { headers: { Authorization: `Bearer ${sessionToken}` }, timeout: 30000 }
        );
        mergeDropped = resp?.data?.dropped_photo_count || 0;
      }
      // Post-merge: emit an Undo-capable toast. sonner's `action`
      // prop renders a button inside the toast — 5-second auto-dismiss
      // matches Gmail's undo-send model. Snapshot captured up top
      // gets replayed on tap. We keep the toast handler self-contained
      // so it works from either mode.
      const doUndo = async () => {
        try {
          if (isLocalMode) {
            // Rebuild target's photos + restore fields, then re-add source
            await localStore.deleteAllPhotos(snapshot.target.plant_id);
            for (let i = 0; i < snapshot.target.local_photos.length; i++) {
              const p = snapshot.target.local_photos[i];
              if (i === 0) await localStore.savePhoto(snapshot.target.plant_id, p.image_base64);
              else await localStore.addPhotoToPlant(snapshot.target.plant_id, p.image_base64);
            }
            await localStore.updatePlant(snapshot.target.plant_id, {
              common_name: snapshot.target.common_name,
              scientific_name: snapshot.target.scientific_name,
              care_details: snapshot.target.care_details,
              incomplete: snapshot.target.incomplete,
              has_photo: (snapshot.target.local_photos || []).length > 0,
            });
            await localStore.addPlant({
              plant_id: snapshot.source.plant_id,
              common_name: snapshot.source.common_name,
              scientific_name: snapshot.source.scientific_name,
              care_details: snapshot.source.care_details,
              incomplete: snapshot.source.incomplete,
              has_photo: (snapshot.source.local_photos || []).length > 0,
              created_at: snapshot.source.created_at,
            });
            for (let i = 0; i < snapshot.source.local_photos.length; i++) {
              const p = snapshot.source.local_photos[i];
              if (i === 0) await localStore.savePhoto(snapshot.source.plant_id, p.image_base64);
              else await localStore.addPhotoToPlant(snapshot.source.plant_id, p.image_base64);
            }
          } else {
            const sessionToken = localStorage.getItem('auth_hint');
            await axios.post(
              `${BACKEND_URL}/api/plants/merge/undo`,
              { target: snapshot.target, source: snapshot.source },
              { headers: { Authorization: `Bearer ${sessionToken}` }, timeout: 30000 }
            );
          }
          toast.success(t('mergeUndoDone') || 'Merge undone.');
          onMerged?.(target.plant_id, source.plant_id);
        } catch (err) {
          const detail = err?.response?.data?.detail || err?.message || 'Undo failed';
          toast.error(`${t('mergeUndoFailed') || 'Undo failed'}: ${detail}`);
        }
      };

      const message = (droppedCount) => droppedCount > 0
        ? (t('mergeSuccessWithDrop') || 'Merged. {n} extra photo(s) discarded (5 max).').replace('{n}', droppedCount)
        : (t('mergeSuccess') || 'Plants merged.');
      toast(message(mergeDropped), {
        duration: 5000,
        action: {
          label: t('undo') || 'Undo',
          onClick: () => { doUndo(); },
        },
      });
      onMerged?.(target.plant_id, source.plant_id);
      onClose?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Merge failed';
      toast.error(`${t('mergeFailed') || 'Merge failed'}: ${detail}`);
    } finally {
      setMerging(false);
    }
  };

  return (
    <Dialog open={!!(target && source)} onOpenChange={(open) => { if (!open) onClose?.(); }}>
      <DialogContent
        className="max-w-2xl lg:max-w-3xl max-h-[90vh] overflow-y-auto"
        aria-describedby={undefined}
        data-testid="merge-plants-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display" style={{ color: 'hsl(143, 28%, 25%)' }}>
            <ArrowRightLeft className="w-5 h-5" />
            {t('mergeDialogTitle') || 'Merge these two plants'}
          </DialogTitle>
          <DialogDescription>
            {(t('mergeDialogBlurbV2') || 'Verify the common and scientific names match, choose which photos to keep, and pick a cover. The other plant will be absorbed.')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Header cards showing the two plants */}
          <div className="grid grid-cols-2 gap-3" data-testid="merge-header-cards">
            <div className="rounded-lg p-3" style={{ backgroundColor: 'hsl(143, 28%, 96%)', border: '1px solid hsl(143, 28%, 85%)' }}>
              <div className="text-[10px] uppercase font-semibold mb-1" style={{ color: 'hsl(143, 28%, 45%)' }}>
                {t('mergeKeeper') || 'Keeper (stays)'}
              </div>
              <div className="font-medium text-sm" style={{ color: 'hsl(143, 28%, 25%)' }}>{target?.common_name}</div>
              <div className="text-xs italic" style={{ color: 'hsl(125, 12%, 40%)' }}>{target?.scientific_name || ''}</div>
              <div className="text-[11px] mt-1" style={{ color: 'hsl(125, 12%, 45%)' }}>
                {targetPhotoCount} {t('mergePhotos') || 'photo(s)'}
              </div>
            </div>
            <div className="rounded-lg p-3" style={{ backgroundColor: 'hsl(21, 60%, 96%)', border: '1px solid hsl(21, 70%, 85%)' }}>
              <div className="text-[10px] uppercase font-semibold mb-1" style={{ color: 'hsl(21, 70%, 45%)' }}>
                {t('mergeAbsorbedTitle') || 'Absorbed (deleted)'}
              </div>
              <div className="font-medium text-sm" style={{ color: 'hsl(21, 40%, 25%)' }}>{source?.common_name}</div>
              <div className="text-xs italic" style={{ color: 'hsl(125, 12%, 40%)' }}>{source?.scientific_name || ''}</div>
              <div className="text-[11px] mt-1" style={{ color: 'hsl(125, 12%, 45%)' }}>
                {sourcePhotoCount} {t('mergePhotos') || 'photo(s)'}
              </div>
            </div>
          </div>

          {/* Names — user can edit if they want */}
          <div className="space-y-3">
            <div>
              <Label htmlFor="merge-common-name">{t('commonName') || 'Common name'} *</Label>
              <Input
                id="merge-common-name"
                value={commonName}
                onChange={(e) => setCommonName(e.target.value)}
                data-testid="merge-common-name-input"
                placeholder={target?.common_name || ''}
              />
            </div>
            <div>
              <Label htmlFor="merge-sci-name">{t('scientificName') || 'Scientific name'}</Label>
              <Input
                id="merge-sci-name"
                value={scientificName}
                onChange={(e) => setScientificName(e.target.value)}
                data-testid="merge-scientific-name-input"
                placeholder={target?.scientific_name || source?.scientific_name || ''}
                className="font-mono"
              />
            </div>
          </div>

          {/* Photo picker grid — one combined pool from both plants.
              Checkboxes let the user uncheck ones they don't want.
              Cap enforced at 5 with an auto-drop toast. Tapping the
              star badge on any tile promotes that photo to cover. */}
          <div className="space-y-2" data-testid="merge-photo-picker">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 justify-between">
              <div className="text-xs font-medium" style={{ color: 'hsl(143, 28%, 25%)' }}>
                {(t('mergePhotoPickerHeader') || 'Photos — pick which ones to keep ({sel}/{max})')
                  .replace('{sel}', selectedKeys.length)
                  .replace('{max}', MAX_PICK)}
              </div>
              {selectedKeys.length > 0 && (
                <div className="text-[10px]" style={{ color: 'hsl(125, 12%, 40%)' }}>
                  {t('mergePhotoPickerCoverHint') || 'The starred photo will be the cover'}
                </div>
              )}
            </div>
            {photosLoading ? (
              <div className="flex items-center gap-2 text-xs py-4" style={{ color: 'hsl(125, 12%, 45%)' }}>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('mergePhotoLoading') || 'Loading photos…'}
              </div>
            ) : photos.length === 0 ? (
              <div className="text-xs rounded-lg px-3 py-2" style={{ backgroundColor: 'hsl(143, 28%, 96%)', color: 'hsl(143, 28%, 25%)' }}>
                {t('mergePhotoNone') || 'Neither plant has any photos.'}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {photos.map((p, idx) => {
                  const selIndex = selectedKeys.indexOf(p.key);
                  const isSelected = selIndex >= 0;
                  const isCover = selIndex === 0;
                  const borderColor = p.source === 'target' ? 'hsl(143, 28%, 55%)' : 'hsl(21, 70%, 55%)';
                  return (
                    <div
                      key={p.key}
                      className="relative rounded-lg overflow-hidden border-2 transition-all"
                      style={{
                        borderColor: isSelected ? borderColor : 'hsl(143, 28%, 88%)',
                        boxShadow: isSelected ? `0 0 0 2px ${borderColor}22` : 'none',
                        aspectRatio: '1 / 1',
                        backgroundColor: 'hsl(143, 28%, 96%)',
                      }}
                      data-testid={`merge-photo-${p.source}-${idx}`}
                    >
                      {p.thumb ? (
                        <img
                          src={p.thumb}
                          alt=""
                          onClick={() => togglePhoto(p.key)}
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          className="w-full h-full object-cover cursor-pointer"
                          style={{ opacity: isSelected ? 1 : 0.55 }}
                          draggable={false}
                        />
                      ) : null}
                      {(!p.thumb) && (
                        <div className="w-full h-full flex items-center justify-center cursor-pointer" onClick={() => togglePhoto(p.key)}>
                          <ImageOff className="w-8 h-8" style={{ color: 'hsl(125, 12%, 50%)' }} />
                        </div>
                      )}
                      {/* Source badge — top-left, tiny */}
                      <div
                        className="absolute top-1 left-1 text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded"
                        style={{
                          backgroundColor: p.source === 'target' ? 'hsla(143, 28%, 25%, 0.85)' : 'hsla(21, 70%, 45%, 0.85)',
                          color: 'white',
                        }}
                      >
                        {p.source === 'target'
                          ? (t('mergeKeepThis') || 'Keeper')
                          : (t('mergeAbsorbed') || 'Absorbed')}
                      </div>
                      {/* Selection number / cover marker */}
                      {isSelected && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); makeCover(p.key); }}
                          className="absolute top-1 right-1 w-6 h-6 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: isCover ? 'hsl(48, 90%, 50%)' : 'white', border: '1.5px solid ' + borderColor }}
                          title={isCover ? (t('mergePhotoIsCover') || 'Cover photo') : (t('mergePhotoMakeCover') || 'Make cover')}
                          data-testid={`merge-photo-cover-${p.source}-${idx}`}
                          aria-label={isCover ? (t('mergePhotoIsCover') || 'Cover photo') : (t('mergePhotoMakeCover') || 'Make cover')}
                        >
                          {isCover
                            ? <Star className="w-3.5 h-3.5" style={{ color: 'white', fill: 'white' }} />
                            : <span className="text-[10px] font-bold" style={{ color: borderColor }}>{selIndex + 1}</span>}
                        </button>
                      )}
                      {/* Zoom / enlarge button. Sits mid-right if the
                          tile is selected (so it doesn't collide with
                          the cover badge), else top-right. Uses
                          stopPropagation so opening the zoom doesn't
                          also toggle keep/skip. */}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setZoomedPhoto(p); }}
                        className="absolute w-6 h-6 rounded-full flex items-center justify-center shadow-sm"
                        style={{
                          top: isSelected ? '32px' : '4px',
                          right: '4px',
                          backgroundColor: 'hsla(0, 0%, 100%, 0.9)',
                          border: `1px solid ${borderColor}`,
                        }}
                        title={t('mergePhotoZoom') || 'View larger'}
                        data-testid={`merge-photo-zoom-${p.source}-${idx}`}
                        aria-label={t('mergePhotoZoom') || 'View larger'}
                      >
                        <ZoomIn className="w-3 h-3" style={{ color: borderColor }} />
                      </button>
                      {/* Toggle overlay button (bottom) */}
                      <button
                        type="button"
                        onClick={() => togglePhoto(p.key)}
                        className="absolute bottom-1 left-1 right-1 text-[10px] font-medium rounded px-2 py-1 text-center"
                        style={{
                          backgroundColor: isSelected ? 'hsla(0, 0%, 100%, 0.9)' : 'hsla(0, 0%, 100%, 0.7)',
                          color: 'hsl(143, 28%, 25%)',
                        }}
                        data-testid={`merge-photo-toggle-${p.source}-${idx}`}
                      >
                        {isSelected ? (t('mergePhotoSelected') || '✓ Keep') : (t('mergePhotoDeselected') || 'Skip')}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            {/* Legend showing which plant each color represents */}
            {photos.length > 0 && (
              <div className="flex items-center gap-3 text-[10px]" style={{ color: 'hsl(125, 12%, 45%)' }}>
                <span className="flex items-center gap-1">
                  <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: 'hsl(143, 28%, 55%)' }} />
                  {targetPhotoCount} {t('mergePhotos') || 'photo(s)'} ({t('mergeKeepThis') || 'Keeper'})
                </span>
                <span className="flex items-center gap-1">
                  <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: 'hsl(21, 70%, 55%)' }} />
                  {sourcePhotoCount} {t('mergePhotos') || 'photo(s)'} ({t('mergeAbsorbed') || 'Absorbed'})
                </span>
              </div>
            )}
          </div>

          {/* Photo warning — surfaces when unselected photos will be
              orphaned (Drive) or deleted (local) by the merge. */}
          {droppedPhotoEstimate > 0 && (
            <div
              className="text-xs rounded-lg px-3 py-2"
              style={{ backgroundColor: 'hsl(40, 70%, 96%)', color: 'hsl(40, 30%, 25%)', border: '1px solid hsl(40, 60%, 82%)' }}
              data-testid="merge-photo-warning"
            >
              {isLocalMode
                ? (t('mergePhotoWarningLocal') || '⚠ {n} unchecked photo(s) will be removed with the merge.').replace('{n}', droppedPhotoEstimate)
                : (t('mergePhotoWarningDrive') || '⚠ {n} unchecked photo(s) will be orphaned (stay in Drive, not attached to any plant).').replace('{n}', droppedPhotoEstimate)}
            </div>
          )}

          <div className="flex gap-2 pt-3 justify-end">
            <Button
              onClick={onClose}
              variant="outline"
              disabled={merging}
              data-testid="merge-cancel-button"
            >
              {t('cancel') || 'Cancel'}
            </Button>
            <Button
              onClick={handleMerge}
              disabled={merging || !commonName.trim() || photosLoading}
              className="gap-1.5"
              style={{ backgroundColor: 'hsl(143, 28%, 25%)', color: 'white' }}
              data-testid="merge-confirm-button"
            >
              {merging
                ? <><Loader2 className="w-4 h-4 animate-spin" />{t('mergeInProgress') || 'Merging…'}</>
                : <><ArrowRightLeft className="w-4 h-4" />{t('mergeConfirm') || 'Merge plants'}</>}
            </Button>
          </div>
        </div>

        {/* Photo zoom overlay — full-screen preview when the user taps a
            magnifier icon. Sits INSIDE the dialog so escape/backdrop
            close the zoom, not the whole merge modal. */}
        {zoomedPhoto && (
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center p-4"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.85)' }}
            onClick={() => setZoomedPhoto(null)}
            data-testid="merge-photo-zoom-overlay"
          >
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setZoomedPhoto(null); }}
              className="absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center"
              style={{ backgroundColor: 'rgba(255,255,255,0.15)', color: 'white' }}
              data-testid="merge-photo-zoom-close"
              aria-label={t('close') || 'Close'}
            >
              <XIcon className="w-5 h-5" />
            </button>
            {zoomedPhoto.thumb ? (
              <img
                src={zoomedPhoto.thumb}
                alt=""
                onClick={(e) => e.stopPropagation()}
                className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                style={{ userSelect: 'none' }}
                draggable={false}
              />
            ) : (
              <div className="text-white text-sm">{t('mergePhotoNone') || 'Photo unavailable.'}</div>
            )}
            <div
              className="absolute bottom-4 left-1/2 -translate-x-1/2 text-xs px-3 py-1.5 rounded-full"
              style={{
                backgroundColor: zoomedPhoto.source === 'target' ? 'hsla(143, 28%, 25%, 0.85)' : 'hsla(21, 70%, 45%, 0.85)',
                color: 'white',
              }}
            >
              {zoomedPhoto.source === 'target'
                ? (t('mergeKeepThis') || 'Keeper')
                : (t('mergeAbsorbed') || 'Absorbed')}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
